import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'update'>('rating');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredComics = useMemo(() => {
    if (!currentCategory) return [];
    let result = comics.filter((c) => c.categorySlug === slug);
    if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      result = [...result].sort((a, b) => b.totalViews - a.totalViews);
    } else {
      result = [...result].sort(
        (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
      );
    }
    return result;
  }, [slug, sortBy, currentCategory]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-600 text-lg">未找到该分类</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 古风漫画网`,
    description: `${currentCategory.description}，古风漫画网为您精选${currentCategory.count}部优质韩国漫画，免费在线阅读。`,
    url: `https://www.gufengmanhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '古风漫画网',
      url: 'https://www.gufengmanhua.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageSchema)}</script>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
            {currentCategory.name}
          </h1>
          <p className="text-sm text-foreground-500">
            {currentCategory.description} · 共 {currentCategory.count} 部作品
          </p>
        </div>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            {(['rating', 'views', 'update'] as const).map((key) => (
              <button
                key={key}
                onClick={() => {
                  setSortBy(key);
                  setCurrentPage(1);
                }}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === key
                    ? 'bg-background-50 text-primary-500 font-medium shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {key === 'rating' && '评分最高'}
                {key === 'views' && '人气最旺'}
                {key === 'update' && '最近更新'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
          {paginatedComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>

        {paginatedComics.length === 0 && (
          <div className="text-center py-20">
            <p className="text-foreground-500">该分类暂无漫画</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  page === currentPage
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </>
  );
}